import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import type { UserExchange } from '../../api/client';
import { parseDbDate } from '../../lib/date';

const SYNC_BADGE: Record<UserExchange['syncStatus'], string> = {
  never: 'badge-neutral',
  syncing: 'badge-warning',
  ok: 'badge-success',
  error: 'badge-error',
};

interface ExchangesTableProps {
  exchanges: UserExchange[];
  syncingId: string | null;
  onSync: (id: string) => void;
}

/**
 * Bağlı exchange'leri senkronizasyon durumu ile tablo şeklinde gösterir.
 */
export function ExchangesTable({ exchanges, syncingId, onSync }: ExchangesTableProps) {
  const { _ } = useLingui();

  const SYNC_LABEL: Record<UserExchange['syncStatus'], string> = {
    never: _({ id: 'dashboard.exchanges.syncStatus.never' }),
    syncing: _({ id: 'dashboard.exchanges.syncStatus.syncing' }),
    ok: _({ id: 'dashboard.exchanges.syncStatus.ok' }),
    error: _({ id: 'dashboard.exchanges.syncStatus.error' }),
  };
  if (exchanges.length === 0) {
    return (
      <div className="card bg-base-100 shadow border border-base-300">
        <div className="card-body items-center text-center py-12">
          <p className="text-base-content/50">
            {_({ id: 'dashboard.exchanges.empty.description' })}
          </p>
          <Link to="/exchanges/new" className="btn btn-primary btn-sm mt-3">
            {_({ id: 'dashboard.exchanges.empty.addButton' })}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow border border-base-300">
      <div className="card-body p-0">
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-base-300">
          <h2 className="card-title text-base">{_({ id: 'dashboard.exchanges.title' })}</h2>
          <Link to="/exchanges" className="btn btn-ghost btn-xs">
            {_({ id: 'dashboard.exchanges.viewAll' })}
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table table-zebra table-pin-rows">
            <thead>
              <tr>
                <th>{_({ id: 'dashboard.exchanges.col.exchange' })}</th>
                <th>{_({ id: 'dashboard.exchanges.col.status' })}</th>
                <th className="hidden sm:table-cell">
                  {_({ id: 'dashboard.exchanges.col.lastSync' })}
                </th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {exchanges.map((ex) => (
                <tr key={ex.id} className="hover">
                  <td>
                    <div className="font-medium">{ex.exchangeName ?? ex.exchangeSlug}</div>
                    {ex.label && <div className="text-xs text-base-content/50">{ex.label}</div>}
                  </td>
                  <td>
                    <span
                      className={`badge badge-sm ${SYNC_BADGE[ex.syncStatus] ?? 'badge-neutral'}`}
                    >
                      {SYNC_LABEL[ex.syncStatus] ?? ex.syncStatus}
                    </span>
                    {ex.syncErrorMsg && (
                      <div
                        className="text-xs text-error mt-0.5 max-w-50 truncate"
                        title={ex.syncErrorMsg}
                      >
                        {ex.syncErrorMsg}
                      </div>
                    )}
                  </td>
                  <td className="hidden sm:table-cell text-xs text-base-content/60">
                    {ex.lastSyncedAt
                      ? parseDbDate(ex.lastSyncedAt).toLocaleDateString('tr-TR')
                      : '—'}
                  </td>
                  <td>
                    <button
                      className="btn btn-outline btn-primary btn-xs gap-1"
                      onClick={() => onSync(ex.id)}
                      disabled={syncingId === ex.id || ex.syncStatus === 'syncing'}
                      aria-label={_({ id: 'dashboard.exchanges.action.syncLabel' })}
                    >
                      {syncingId === ex.id ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : (
                        <RefreshCw size={12} strokeWidth={2.25} aria-hidden="true" />
                      )}
                      {_({ id: 'dashboard.exchanges.action.syncButton' })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
